import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getToken } from "../../utils/auth";
import {
  availableCards,
  availableInstallments,
  formatArs,
  normalizeCardCatalog,
  selectionAfterPaymentChange,
} from "./calculatorRules";
import "./ZocoPagosCalculator.css";

const initialSelection = {
  channel: "",
  payment: "",
  card: "",
  installments: null,
  accreditation: "",
  province: "",
};

const channels = [
  { value: "ZocoPOS", label: "Zoco POS", enabled: true },
  { value: "QR", label: "QR", enabled: false },
  { value: "Link", label: "Link de pago", enabled: false },
  { value: "Ecommerce", label: "e-Commerce", enabled: false },
];

const payments = [
  { value: "Credito", label: "Tarjeta de crédito", enabled: true },
  { value: "Debito", label: "Tarjeta de débito", enabled: true },
  { value: "Prepaga", label: "Tarjeta prepaga", enabled: false },
];

const steps = ["Cobro", "Pago", "Cuotas", "Acreditación", "Provincia"];

const getErrorMessage = async (response) => {
  const text = await response.text();
  if (!text) return "No pudimos calcular la operación.";
  try {
    const parsed = JSON.parse(text);
    return parsed.title || parsed.message || text;
  } catch {
    return text.replace(/^"|"$/g, "");
  }
};

const InfoTip = ({ children }) => (
  <span className="zpc-tip">
    <button type="button" aria-label={`Información: ${children}`}>i</button>
    <span role="tooltip">{children}</span>
  </span>
);

const Choice = ({ children, selected, disabled, onClick, note }) => (
  <button
    type="button"
    className={`zpc-choice ${selected ? "is-selected" : ""}`}
    disabled={disabled}
    onClick={onClick}
    aria-pressed={selected}
  >
    <span>{children}</span>
    {note && <small>{note}</small>}
  </button>
);

const ZocoPagosCalculator = () => {
  const [step, setStep] = useState(0);
  const [amount, setAmount] = useState("100000");
  const [selection, setSelection] = useState(initialSelection);
  const [catalog, setCatalog] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(true);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoadingOptions(true);
      setError("");
      try {
        const [cardsResponse, provincesResponse] = await Promise.all([
          fetch(process.env.REACT_APP_API_CALCULADORA_TARJETA || "/api/calculadora/datostarjeta"),
          fetch(process.env.REACT_APP_API_PROVINCIAS || "/api/provincia/listaprovincia"),
        ]);
        if (!cardsResponse.ok || !provincesResponse.ok) {
          throw new Error("No pudimos cargar las tasas o provincias configuradas.");
        }
        const [cardsData, provincesData] = await Promise.all([
          cardsResponse.json(),
          provincesResponse.json(),
        ]);
        if (active) {
          setCatalog(normalizeCardCatalog(cardsData));
          setProvinces((Array.isArray(provincesData) ? provincesData : [])
            .map((item) => item.provincia ?? item.Provincia)
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b, "es")));
        }
      } catch (loadError) {
        if (active) setError(loadError.message);
      } finally {
        if (active) setLoadingOptions(false);
      }
    };
    load();
    return () => { active = false; };
  }, []);

  const cards = useMemo(
    () => availableCards(catalog, selection.payment),
    [catalog, selection.payment],
  );
  const installments = useMemo(
    () => availableInstallments(catalog, selection.card, selection.payment),
    [catalog, selection.card, selection.payment],
  );
  const amountNumber = Number(amount);
  const isAmountValid = Number.isFinite(amountNumber) && amountNumber > 0;
  const isComplete = isAmountValid && Object.values(selection).every((value) => value !== "" && value !== null);

  const calculate = useCallback(async () => {
    if (!isComplete) return;
    setCalculating(true);
    setError("");
    try {
      const response = await fetch(
        process.env.REACT_APP_API_CALCULADORA_USUARIOS || "/api/calculadora/calculadorausuarios",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            Token: getToken(),
            Monto: amountNumber.toString(),
            Cuota: selection.installments.toString(),
            TipoNetoBruto: "Bruto",
            TipoDebCred: selection.payment,
            TipoTarjeta: selection.card.toLowerCase(),
            Tarjeta: selection.card,
            TipoCuota: "normal",
            Provincia: selection.province,
          }),
        },
      );
      if (!response.ok) throw new Error(await getErrorMessage(response));
      setResult(await response.json());
    } catch (calculationError) {
      setResult(null);
      setError(calculationError.message);
    } finally {
      setCalculating(false);
    }
  }, [amountNumber, isComplete, selection]);

  useEffect(() => {
    if (!isComplete) {
      setResult(null);
      return undefined;
    }
    const timer = setTimeout(calculate, 250);
    return () => clearTimeout(timer);
  }, [calculate, isComplete]);

  const chooseChannel = (channel) => {
    setSelection({ ...initialSelection, channel });
    setResult(null);
    setStep(1);
  };

  const choosePayment = (payment) => {
    setSelection((current) => selectionAfterPaymentChange(current, payment));
    setResult(null);
  };

  const chooseCard = (card) => {
    setSelection((current) => ({ ...current, card, installments: null, accreditation: "", province: "" }));
    setResult(null);
    setStep(2);
  };

  const chooseInstallments = (value) => {
    setSelection((current) => ({ ...current, installments: value, accreditation: "", province: "" }));
    setResult(null);
    setStep(3);
  };

  const chooseAccreditation = (value) => {
    setSelection((current) => ({ ...current, accreditation: value, province: "" }));
    setResult(null);
    setStep(4);
  };

  const reset = () => {
    setSelection(initialSelection);
    setAmount("100000");
    setResult(null);
    setError("");
    setStep(0);
  };

  const canVisitStep = (target) => target === 0
    || (target === 1 && selection.channel)
    || (target === 2 && selection.card)
    || (target === 3 && selection.installments !== null)
    || (target === 4 && selection.accreditation);

  const accreditationLabel = ["cabal", "naranja", "maestro"].includes(selection.card.toLowerCase())
    ? "Plazo estándar según convenio"
    : "Acreditación anticipada configurada";

  const pillValues = [
    selection.channel,
    selection.payment ? `${selection.payment === "Credito" ? "Crédito" : "Débito"}${selection.card ? ` · ${selection.card}` : ""}` : "",
    selection.installments === 0 ? "Contado" : selection.installments ? `${selection.installments} cuota${selection.installments > 1 ? "s" : ""}` : "",
    selection.accreditation ? accreditationLabel : "",
    selection.province,
  ];

  const breakdown = result?.desglose;
  const detailRows = breakdown ? [
    ["Arancel", breakdown.arancel, "Comisión configurada para la tarjeta y modalidad seleccionadas."],
    ["IVA del arancel", breakdown.ivaArancel, "IVA derivado de la comisión configurada en Tasas Zoco."],
    ["Costo financiero", breakdown.costoFinanciero, "Costo financiero correspondiente a la cantidad de cuotas elegida."],
    ["IVA del costo financiero", breakdown.ivaCostoFinanciero, "Componente impositivo del costo financiero."],
    ["Costo de anticipación", breakdown.costoAnticipacion, "Costo de acreditación anticipada definido por el convenio."],
    ["IVA de anticipación", breakdown.ivaCostoAnticipacion, "Componente impositivo del costo de anticipación."],
    ["Retención de Ingresos Brutos", breakdown.retencionIngresosBrutos, `Alícuota configurada para ${selection.province}.`],
    ["Otras retenciones", breakdown.otrasRetenciones, "Retenciones de IVA, Ganancias o municipales que correspondan."],
    ["Impuesto a débitos y créditos", breakdown.impuestoDebitosCreditos, "Tasa impositiva configurada para la operación."],
  ].filter(([, value]) => Number(value) !== 0) : [];

  const questionContent = [
    <div className="zpc-grid zpc-grid-four" key="channels">
      {channels.map((option) => (
        <Choice key={option.value} selected={selection.channel === option.value} disabled={!option.enabled}
          note={!option.enabled ? "Sin configuración comercial" : ""} onClick={() => chooseChannel(option.value)}>
          {option.label}
        </Choice>
      ))}
    </div>,
    <div key="payments">
      <div className="zpc-grid zpc-grid-three">
        {payments.map((option) => (
          <Choice key={option.value} selected={selection.payment === option.value} disabled={!option.enabled}
            note={!option.enabled ? "Sin tasas configuradas" : ""} onClick={() => choosePayment(option.value)}>
            {option.label}
          </Choice>
        ))}
      </div>
      {selection.payment && (
        <fieldset className="zpc-card-fieldset">
          <legend>Seleccioná la tarjeta</legend>
          <div className="zpc-card-list">
            {cards.map((card) => (
              <Choice key={card.name} selected={selection.card === card.name} onClick={() => chooseCard(card.name)}>
                {card.name}
              </Choice>
            ))}
          </div>
          {!loadingOptions && cards.length === 0 && <p>No hay tarjetas configuradas para este medio de pago.</p>}
        </fieldset>
      )}
    </div>,
    <div className="zpc-grid zpc-installments" key="installments">
      {installments.map((value) => (
        <Choice key={value} selected={selection.installments === value} onClick={() => chooseInstallments(value)}>
          {value === 0 || value === 1 ? "Contado" : value}
        </Choice>
      ))}
    </div>,
    <div className="zpc-grid zpc-grid-one" key="accreditation">
      <Choice selected={Boolean(selection.accreditation)} onClick={() => chooseAccreditation("Convenio") }>
        {accreditationLabel}
      </Choice>
      <p className="zpc-config-note">La disponibilidad y el costo provienen del convenio configurado para esta tarjeta. No hay alternativas adicionales habilitadas.</p>
    </div>,
    <div className="zpc-province" key="province">
      <label htmlFor="zpc-province">Provincia</label>
      <select id="zpc-province" value={selection.province}
        onChange={(event) => setSelection((current) => ({ ...current, province: event.target.value }))}>
        <option value="">Seleccioná tu provincia</option>
        {provinces.map((province) => <option key={province} value={province}>{province}</option>)}
      </select>
      <p>El resultado es estimativo e incluye la alícuota configurada para la provincia seleccionada.</p>
    </div>,
  ];

  const titles = [
    ["¿Cómo querés cobrar?", "Seleccioná el medio de cobro de la operación."],
    ["¿Cómo te van a pagar?", "Seleccioná el medio de pago y la tarjeta."],
    ["¿En cuántas cuotas?", "Seleccioná únicamente entre las cantidades habilitadas."],
    ["¿Cuándo querés recibir el dinero?", "Seleccioná el plazo de acreditación disponible."],
    ["¿En qué provincia operás?", "Seleccioná la provincia correspondiente."],
  ];

  return (
    <section className="zpc" aria-label="Calculadora de cobros Zoco Pagos">
      <div className="zpc-header">
        <div className="zpc-amount">
          <label htmlFor="zpc-amount">Monto de la venta</label>
          <div><span>$</span><input id="zpc-amount" type="number" min="1" step="0.01" inputMode="decimal"
            value={amount} onChange={(event) => { setAmount(event.target.value); setResult(null); }} /></div>
          {!isAmountValid && <small role="alert">Ingresá un importe mayor a cero.</small>}
        </div>
        <div className="zpc-pills" aria-label="Resumen de elecciones">
          {steps.map((label, index) => (
            <button type="button" key={label} className={`${step === index ? "is-current" : ""} ${pillValues[index] ? "is-complete" : ""}`}
              disabled={!canVisitStep(index)} onClick={() => setStep(index)}>
              {pillValues[index] || label}
            </button>
          ))}
        </div>
        <div className="zpc-net" aria-live="polite">
          <span>Vas a recibir</span>
          <strong>{calculating ? "Calculando…" : formatArs(breakdown?.netoEstimado)}</strong>
          {breakdown && <small>Total de descuentos: {formatArs(breakdown.totalDescuentos)}</small>}
        </div>
      </div>

      {breakdown && (
        <div className="zpc-details">
          <button type="button" className="zpc-details-toggle" onClick={() => setDetailsOpen((open) => !open)} aria-expanded={detailsOpen}>
            Detalle de costos <span aria-hidden="true">{detailsOpen ? "⌃" : "⌄"}</span>
          </button>
          {detailsOpen && (
            <div className="zpc-detail-table">
              <h3>Detalle de costos</h3>
              {detailRows.map(([label, value, explanation]) => (
                <div className="zpc-detail-row" key={label}>
                  <span>{label} <InfoTip>{explanation} Importe: {formatArs(value)}.</InfoTip></span>
                  <strong>{formatArs(value)}</strong>
                </div>
              ))}
              <div className="zpc-detail-row is-total"><span>Total de descuentos</span><strong>{formatArs(breakdown.totalDescuentos)}</strong></div>
            </div>
          )}
        </div>
      )}

      <div className="zpc-question">
        <div className="zpc-question-heading">
          <span>Paso {step + 1} de 5</span>
          <h2>{titles[step][0]}</h2>
          <p>{titles[step][1]}</p>
        </div>
        <div className="zpc-question-body">
          <button type="button" className="zpc-arrow" aria-label="Paso anterior" disabled={step === 0} onClick={() => setStep((value) => value - 1)}>‹</button>
          <div className="zpc-options" key={step}>{loadingOptions && step > 0 ? <p>Cargando opciones…</p> : questionContent[step]}</div>
          <button type="button" className="zpc-arrow" aria-label="Paso siguiente" disabled={step === 4 || !canVisitStep(step + 1)} onClick={() => setStep((value) => value + 1)}>›</button>
        </div>
        {error && <div className="zpc-error" role="alert">{error}</div>}
      </div>
      <button type="button" className="zpc-reset" onClick={reset}>Reiniciar cálculo</button>
    </section>
  );
};

export default ZocoPagosCalculator;
